import { useState, useEffect, useCallback, useRef } from "react";
import { Download, Search } from "lucide-react";
import { DataTable, Column } from "../components/DataTable";
import { useToastStore } from "../../stores/toastStore";
import { useAuthStore } from "../../stores/authStore";
import { Dropdown } from "../components/Dropdown";
import type { AuditLogItem } from "../../api/frameworksApi";

const MODULE_OPTIONS = [
  { value: "", label: "All Modules" },
  { value: "cmdb", label: "CMDB" },
  { value: "assessment_plan", label: "Assessment Plan" },
  { value: "user", label: "User" },
  { value: "role_mapping", label: "Role Mapping" },
  { value: "ssp", label: "SSP" },
  { value: "poam", label: "POAM" },
  { value: "assessment_execution", label: "Assessment Execution" },
  { value: "assessment_review", label: "Assessment Review" },
];

const PAGE_SIZE_OPTIONS = [
  { value: 10, label: "10" },
  { value: 25, label: "25" },
  { value: 50, label: "50" },
  { value: 100, label: "100" },
];

const formatTimestamp = (isoString: string): string => {
  try {
    const date = new Date(isoString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return isoString;
  }
};

// Known acronyms that should stay fully uppercase when a raw enum is formatted.
const LABEL_ACRONYMS = new Set([
  "cmdb",
  "ssp",
  "poam",
  "api",
  "mfa",
  "sso",
  "ldap",
  "rbac",
]);

// Turn a raw snake_case enum (e.g. "role_mapping") into a readable label
// ("Role Mapping"), keeping known acronyms uppercase ("ssp" -> "SSP").
const formatLabel = (value: string): string => {
  return (value ?? "")
    .split("_")
    .map((word) =>
      LABEL_ACRONYMS.has(word.toLowerCase())
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(" ");
};

const getModuleColor = (module: string): string => {
  const colors: Record<string, string> = {
    cmdb: "bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-400",
    assessment_plan:
      "bg-purple-100 dark:bg-purple-500/15 text-purple-700 dark:text-purple-400",
    user: "bg-green-100 dark:bg-green-500/15 text-green-700 dark:text-green-400",
    role_mapping:
      "bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400",
    ssp: "bg-indigo-100 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-400",
    poam: "bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-400",
    assessment_execution:
      "bg-cyan-100 dark:bg-cyan-500/15 text-cyan-700 dark:text-cyan-400",
    assessment_review:
      "bg-orange-100 dark:bg-orange-500/15 text-orange-700 dark:text-orange-400",
  };
  return (
    colors[module] ||
    "bg-gray-100 dark:bg-gray-500/15 text-gray-700 dark:text-gray-400"
  );
};

const getActionColor = (action: string): string => {
  const colors: Record<string, string> = {
    create:
      "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
    update: "bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-400",
    delete: "bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-400",
    export:
      "bg-purple-100 dark:bg-purple-500/15 text-purple-700 dark:text-purple-400",
    import:
      "bg-indigo-100 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-400",
    approve:
      "bg-green-100 dark:bg-green-500/15 text-green-700 dark:text-green-400",
    submit:
      "bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400",
  };
  return (
    colors[action] ||
    "bg-gray-100 dark:bg-gray-500/15 text-gray-700 dark:text-gray-400"
  );
};

interface AuditLogResponse {
  status: "success" | "error";
  data?: {
    content: AuditLogItem[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
    numberOfElements: number;
    first: boolean;
    last: boolean;
    empty: boolean;
    pageable: {
      offset: number;
      pageNumber: number;
      pageSize: number;
    };
  };
  error?: {
    code: string;
    message: string;
    details: string | null;
  };
}

export function AuditLogs() {
  const toastStore = useToastStore();
  const { getToken } = useAuthStore();

  const [auditLogs, setAuditLogs] = useState<AuditLogItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedModule, setSelectedModule] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [totalItems, setTotalItems] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce the search box: fetch once the user stops typing and jump back to
  // the first page of results.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setCurrentPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Full trail cached lazily. The backend has no free-text search, so when the
  // user searches (or filters) we load everything once and filter client-side;
  // plain browsing stays on server-side pagination (one call per page).
  const allLogsRef = useRef<AuditLogItem[] | null>(null);

  const fetchAuditLogs = useCallback(async () => {
    try {
      setLoading(true);
      const token = getToken();

      if (!token) {
        toastStore.showError("Error", "Authentication token not found");
        return;
      }

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      const hasFilter =
        !!debouncedSearch ||
        !!selectedModule ||
        !!selectedUserId.trim() ||
        !!fromDate ||
        !!toDate;

      if (hasFilter) {
        // Filter/search mode: the backend can't free-text search, so load the
        // full trail once (cached, pages fetched in parallel), then filter and
        // paginate client-side.
        let data = allLogsRef.current;
        if (!data) {
          const feedUrl = (page: number) =>
            `${window.BE_URL || ""}/api/v1/audit-log/feed?page=${page}&size=200`;
          const firstRes = await fetch(feedUrl(0), { headers });
          const firstJson = (await firstRes.json()) as AuditLogResponse;
          if (firstJson.status !== "success" || !firstJson.data) {
            toastStore.showError(
              "Error",
              firstJson.error?.message || "Failed to load audit logs",
            );
            setAuditLogs([]);
            setTotalItems(0);
            return;
          }
          const all: AuditLogItem[] = [...firstJson.data.content];
          const totalPages = firstJson.data.totalPages ?? 1;
          if (totalPages > 1) {
            const rest = await Promise.all(
              Array.from({ length: totalPages - 1 }, (_, i) =>
                fetch(feedUrl(i + 1), { headers })
                  .then((r) => r.json() as Promise<AuditLogResponse>)
                  .then((j) =>
                    j.status === "success" && j.data ? j.data.content : [],
                  )
                  .catch(() => [] as AuditLogItem[]),
              ),
            );
            for (const chunk of rest) all.push(...chunk);
          }
          data = all;
          allLogsRef.current = all;
        }

        const q = debouncedSearch.toLowerCase();
        const uid = selectedUserId.trim().toLowerCase();
        const fromTs = fromDate
          ? new Date(fromDate + "T00:00:00Z").getTime()
          : null;
        const toTs = toDate ? new Date(toDate + "T23:59:59Z").getTime() : null;
        const filtered = data.filter((log) => {
          if (selectedModule && log.module !== selectedModule) return false;
          if (uid && !(log.userId ?? "").toLowerCase().includes(uid))
            return false;
          const ts = new Date(log.createdAt).getTime();
          if (fromTs !== null && ts < fromTs) return false;
          if (toTs !== null && ts > toTs) return false;
          if (q) {
            const text = [
              log.displayName,
              log.userId,
              log.module,
              formatLabel(log.module),
              formatLabel(log.actionType),
              log.actionType,
              log.changeSourceDetail,
              formatTimestamp(log.createdAt),
            ]
              .filter(Boolean)
              .join(" ")
              .toLowerCase();
            if (!text.includes(q)) return false;
          }
          return true;
        });

        setTotalItems(filtered.length);
        setAuditLogs(
          filtered.slice(currentPage * pageSize, (currentPage + 1) * pageSize),
        );
      } else {
        // Browse mode: one server call for just the current page.
        const params = new URLSearchParams();
        params.set("page", String(currentPage));
        params.set("size", String(pageSize));
        const apiUrl = `${window.BE_URL || ""}/api/v1/audit-log/feed?${params.toString()}`;
        const response = await fetch(apiUrl, { headers });
        const json = (await response.json()) as AuditLogResponse;

        if (json.status !== "success" || !json.data) {
          toastStore.showError(
            "Error",
            json.error?.message || "Failed to load audit logs",
          );
          setAuditLogs([]);
          setTotalItems(0);
          return;
        }

        setAuditLogs(json.data.content ?? []);
        setTotalItems(json.data.totalElements ?? 0);
      }
    } catch (error: any) {
      console.error("Failed to fetch audit logs:", error);
      toastStore.showError(
        "Error",
        error.message || "Failed to load audit logs",
      );
      setAuditLogs([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [
    currentPage,
    pageSize,
    selectedModule,
    selectedUserId,
    fromDate,
    toDate,
    debouncedSearch,
    getToken,
    toastStore,
  ]);

  // Refetch whenever the page, page size, or any filter/search changes.
  useEffect(() => {
    fetchAuditLogs();
  }, [fetchAuditLogs]);

  const columns: Column<AuditLogItem>[] = [
    {
      key: "displayName",
      header: "Username",
      align: "left",
      render: (row) => (
        <span className="text-sm">
          {row.displayName || <span className="text-xs d italic">System</span>}
        </span>
      ),
    },
    {
      key: "module",
      header: "Module",
      align: "center",
      render: (row) => (
        <span
          className={`inline-flex items-center justify-center whitespace-nowrap min-w-[150px] text-xs font-medium px-2 py-0.5 rounded ${getModuleColor(row.module)}`}
        >
          {formatLabel(row.module)}
        </span>
      ),
    },
    {
      key: "actionType",
      header: "Action",
      align: "center",
      render: (row) => (
        <span
          className={`inline-flex items-center justify-center whitespace-nowrap min-w-[88px] text-xs font-medium px-2 py-0.5 rounded ${getActionColor(row.actionType)}`}
        >
          {formatLabel(row.actionType)}
        </span>
      ),
    },
    {
      key: "changeSourceDetail",
      header: "Detail",
      align: "left",
      ellipsis: 2,
      tooltip: (row) => row.changeSourceDetail,
      render: (row) => (
        <span className="text-sm">
          {row.changeSourceDetail}
        </span>
      ),
    },
    {
      key: "createdAt",
      header: "Date",
      align: "left",
      render: (row) => (
        <span className="text-sm font-medium">
          {formatTimestamp(row.createdAt)}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1>Audit Logs</h1>
          <p className="text-sm d mt-0.5">
            Complete audit trail of all platform activity
          </p>
        </div>
        {/* <button className="self-start sm:self-auto flex items-center gap-2 px-3 py-1.5 rounded-md border border-border text-sm hover:bg-accent transition-colors flex-shrink-0">
          <Download className="size-4" />
          Export
        </button> */}
      </div>

      {/* Filters */}
      {/* <div className="space-y-3 rounded-lg border border-border bg-card p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">

          <div className="flex flex-col gap-2">
            <label className="text-xs font-medium d">Module</label>
            <Dropdown
              size="sm"
              variant="form"
              value={selectedModule}
              onChange={(v) => handleFilterChange(() => setSelectedModule(v))}
              options={MODULE_OPTIONS}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-xs font-medium d">User ID</label>
            <input
              type="text"
              placeholder="Enter user ID"
              value={selectedUserId}
              onChange={(e) =>
                handleFilterChange(() => setSelectedUserId(e.target.value))
              }
              className="px-3 py-1.5 rounded-md border border-border bg-card text-sm outline-none focus:ring-2 focus:ring-foreground"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-xs font-medium d">From Date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) =>
                handleFilterChange(() => setFromDate(e.target.value))
              }
              className="px-3 py-1.5 rounded-md border border-border bg-card text-sm outline-none focus:ring-2 focus:ring-foreground"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-xs font-medium d">To Date</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) =>
                handleFilterChange(() => setToDate(e.target.value))
              }
              className="px-3 py-1.5 rounded-md border border-border bg-card text-sm outline-none focus:ring-2 focus:ring-foreground"
            />
          </div>
        </div>

        {(selectedModule || selectedUserId || fromDate || toDate) && (
          <div className="flex justify-end">
            <button
              onClick={() => {
                setSelectedModule("");
                setSelectedUserId("");
                setFromDate("");
                setToDate("");
                setCurrentPage(0);
              }}
              className="text-xs px-3 py-1 rounded-md border border-border hover:bg-accent transition-colors text-d"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div> */}

      {/* Search + Rows per page */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 d/50 pointer-events-none" />
          <input
            type="text"
            placeholder="Search audit logs"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm bg-card border border-border rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:focus:border-indigo-500 transition-all placeholder:d/50"
          />
        </div>

        <div className="flex items-center gap-2 ml-auto">
          <span className="text-sm text-[#475569] dark:text-white/60">
            Rows
          </span>
          <select
            value={String(pageSize)}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(0);
            }}
            className="h-[38px] text-sm px-3 pr-8 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] bg-[#f5f7fb] dark:bg-white/[0.04] text-[#0f172a] dark:text-white focus:outline-none focus:border-indigo-500 dark:focus:border-indigo-500 cursor-pointer appearance-none bg-no-repeat"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")",
              backgroundPosition: "right 8px center",
            }}
          >
            {PAGE_SIZE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={auditLogs}
        loading={loading}
        rowKey={(r) => r.id}
        emptyText={
          searchQuery.trim() ||
          selectedModule ||
          selectedUserId ||
          fromDate ||
          toDate
            ? "No audit logs match your filters."
            : "No audit logs found."
        }
        page={currentPage}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={(p) => setCurrentPage(Math.max(0, p))}
        itemLabel="audit logs"
      />
    </div>
  );
}
